import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import {
  PLAN_SELECT_OPTIONS,
  applyPlanDefaults,
  getPlanById,
  makePlanOptionValue,
  parsePlanOptionValue,
} from "../../Constants/planCatalog";

const UserModal = ({ user, onClose, onStatusChange, onPlanChange }) => {
  const [selectedOption, setSelectedOption] = useState("Free|monthly");
  const [savingPlan, setSavingPlan] = useState(false);

  useEffect(() => {
    if (!user) return;
    const plan = getPlanById(user.subscription?.plan || "Free").id;
    const cycle = (user.subscription?.billingCycle || "monthly").toLowerCase();
    const matched = PLAN_SELECT_OPTIONS.find(
      (o) => o.planId === plan && o.billingCycle === cycle,
    );
    setSelectedOption(
      matched?.value || makePlanOptionValue(plan, cycle === "yearly" ? "yearly" : "monthly"),
    );
  }, [user]);

  const { planId, billingCycle } = useMemo(
    () => parsePlanOptionValue(selectedOption),
    [selectedOption],
  );

  const planDefaults = useMemo(
    () => applyPlanDefaults(planId, billingCycle),
    [planId, billingCycle],
  );

  const hasPlanChanges = useMemo(() => {
    if (!user) return false;
    const currentPlan = user.subscription?.plan || "Free";
    const currentCycle = (
      user.subscription?.billingCycle || "monthly"
    ).toLowerCase();
    return planId !== currentPlan || billingCycle !== currentCycle;
  }, [user, planId, billingCycle]);

  if (!user) return null;

  const theme = {
    primary: "#0B1C2D",
    subtext: "#64748B",
    border: "#E2E8F0",
    surface: "#F8FAFC",
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleConfirmPlanUpdate = async () => {
    const next = applyPlanDefaults(planId, billingCycle);

    const { isConfirmed } = await Swal.fire({
      title: "Confirm Plan Change",
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.6;color:#334155">
          <p><strong>${user.companyName || user.name || "Company"}</strong></p>
          <p>Current: <b>${user.subscription?.plan || "Free"}</b>
            (${user.subscription?.billingCycle || "—"}, ${user.subscription?.formLimit ?? 0} projects)</p>
          <p>New: <b style="color:#E63946">${next.plan}</b>
            (${next.billingLabel}, ${next.formLimit} projects · ${next.priceLabel})</p>
          <p>Expires: <b>${formatDate(next.expiresAt)}</b></p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, update plan",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0B1C2D",
      cancelButtonColor: "#94a3b8",
      reverseButtons: true,
    });

    if (!isConfirmed) return;

    setSavingPlan(true);
    try {
      if (onPlanChange) {
        await onPlanChange(user.id, {
          plan: next.plan,
          billingCycle: next.billingCycle,
          formLimit: Number(next.formLimit),
          expiresAt: next.expiresAt,
          price: next.price,
        });
      }
    } finally {
      setSavingPlan(false);
    }
  };

  const paymentRows = user.paymentHistory
    ? Object.values(user.paymentHistory)
    : [];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-0 sm:p-4 sm:p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
          onClick={onClose}
        />

        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-white rounded-t-[28px] sm:rounded-[2rem] w-full max-w-4xl relative z-10 overflow-hidden shadow-2xl flex flex-col h-[92dvh] sm:h-auto sm:max-h-[90vh] border border-white/20"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {/* Header Banner */}
          <div
            style={{ backgroundColor: theme.primary }}
            className="h-28 sm:h-32 flex-shrink-0 relative overflow-hidden flex items-center px-5 sm:px-10"
          >
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-400 to-transparent" />

            <div className="relative z-10 flex items-center gap-4 sm:gap-5 min-w-0 pr-12">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center overflow-hidden shrink-0">
                {user.profilePicURL ? (
                  <img
                    src={user.profilePicURL}
                    className="w-full h-full object-cover"
                    alt=""
                  />
                ) : (
                  <span className="text-2xl font-black text-white">
                    {(user.companyName || "C").charAt(0)}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-none truncate">
                  {user.companyName || "Untitled Company"}
                </h2>
                <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest mt-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  {user.businessType || "General"} • {user.country || "Global"}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="absolute top-5 right-5 w-10 h-10 rounded-xl bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition-all z-20 active:scale-95"
            >
              <span className="material-symbols-outlined text-[22px]">
                close
              </span>
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-10 py-6 sm:py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 1. ADMIN & IDENTITY */}
              <div className="md:col-span-1 space-y-6">
                <section>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Primary Admin
                  </h4>
                  <div className="bg-slate-50 p-5 rounded-[1.5rem] border border-slate-100 space-y-4">
                    <InfoRow icon="person" label="Admin Name" value={user.name} />
                    <InfoRow
                      icon="alternate_email"
                      label="Email"
                      value={user.email}
                    />
                    <InfoRow
                      icon="smartphone"
                      label="Mobile"
                      value={user.mobile}
                    />
                    <InfoRow
                      icon="language"
                      label="Website"
                      value={user.websiteUrl}
                      isLink
                    />
                  </div>
                </section>
              </div>

              {/* 2. SUBSCRIPTION & PLAN DETAILS */}
              <div className="md:col-span-2 space-y-6">
                <section>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    Subscription Asset
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-indigo-50/30 p-5 rounded-[1.5rem] border border-indigo-100/50">
                    <InfoRow
                      icon="layers"
                      label="Active Plan"
                      value={user.subscription?.plan || "Free"}
                    />
                    <InfoRow
                      icon="history"
                      label="Billing Cycle"
                      value={user.subscription?.billingCycle}
                    />
                    <InfoRow
                      icon="event_upcoming"
                      label="Expires At"
                      value={formatDate(user.subscription?.expiresAt)}
                    />
                    <InfoRow
                      icon="database"
                      label="Form Limit"
                      value={`${user.subscription?.formLimit || 0} active projects`}
                    />
                  </div>
                </section>

                {/* Modify Plan — single select, rest auto-set */}
                <section className="bg-white border border-slate-200 rounded-[1.5rem] p-5 sm:p-6 shadow-sm">
                  <div className="mb-5">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E63946]" />
                      Modify User Plan
                    </h4>
                    <p className="text-xs text-slate-500 mt-1.5 font-medium">
                      Choose one plan — projects, price, billing &amp; expiry
                      auto-set.
                    </p>
                  </div>

                  <label className="block space-y-1.5 mb-4">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                      Select Plan
                    </span>
                    <div className="relative">
                      <select
                        value={selectedOption}
                        onChange={(e) => setSelectedOption(e.target.value)}
                        className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-bold text-[#0B1C2D] outline-none focus:ring-2 focus:ring-[#0B1C2D]/15 focus:border-[#0B1C2D]"
                      >
                        {PLAN_SELECT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[20px]">
                        expand_more
                      </span>
                    </div>
                  </label>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    <AutoField
                      label="Billing"
                      value={planDefaults.billingLabel}
                      icon="history"
                    />
                    <AutoField
                      label="Projects"
                      value={`${planDefaults.formLimit}`}
                      icon="folder_open"
                    />
                    <AutoField
                      label="Price"
                      value={planDefaults.priceLabel}
                      icon="payments"
                    />
                    <AutoField
                      label="Expires"
                      value={formatDate(planDefaults.expiresAt)}
                      icon="event_upcoming"
                    />
                  </div>

                  <button
                    type="button"
                    disabled={savingPlan || !hasPlanChanges}
                    onClick={handleConfirmPlanUpdate}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#0B1C2D] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#152a3f] disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {savingPlan ? "progress_activity" : "verified"}
                    </span>
                    {savingPlan ? "Updating…" : "Update Plan"}
                  </button>
                </section>
              </div>

              {/* 3. PAYMENT HISTORY */}
              <div className="md:col-span-3 mt-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-1">
                  Recent Payment History
                </h4>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {paymentRows.length > 0 ? (
                    paymentRows.map((pay, i) => (
                      <div
                        key={i}
                        className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                      >
                        <div className="flex justify-between gap-2 mb-2">
                          <span className="text-xs font-bold text-slate-700">
                            {pay.plan || "—"}
                          </span>
                          <span className="text-xs font-black text-slate-900">
                            ₹{pay.amount || 0}
                          </span>
                        </div>
                        <p className="text-[10px] font-mono text-slate-400 truncate mb-1">
                          {pay.id}
                        </p>
                        <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase">
                          <span>{formatDate(pay.date)}</span>
                          <span>{pay.gateway || "—"}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-slate-100 py-10 text-center text-slate-400 text-sm font-medium">
                      No payment records found
                    </div>
                  )}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block rounded-[1.5rem] border border-slate-100 overflow-hidden bg-white shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase">
                      <tr>
                        <th className="px-6 py-4">Transaction ID</th>
                        <th className="px-6 py-4">Plan / Type</th>
                        <th className="px-6 py-4">Date</th>
                        <th className="px-6 py-4">Gateway</th>
                        <th className="px-6 py-4 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {paymentRows.length > 0 ? (
                        paymentRows.map((pay, i) => (
                          <tr
                            key={i}
                            className="hover:bg-slate-50/50 transition-colors"
                          >
                            <td className="px-6 py-4 text-xs font-bold text-slate-500 font-mono">
                              {pay.id}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                <span className="text-xs font-bold text-slate-700">
                                  {pay.plan}
                                </span>
                                <span className="text-[9px] text-slate-400 font-black uppercase">
                                  {pay.type}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-600">
                              {formatDate(pay.date)}
                            </td>
                            <td className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase">
                              {pay.gateway}
                            </td>
                            <td className="px-6 py-4 text-xs font-black text-slate-900 text-right">
                              ₹{pay.amount || 0}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-6 py-10 text-center text-slate-400 font-medium"
                          >
                            No payment records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-slate-100 bg-white flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => onStatusChange(user.id, !user.status)}
              className={`flex-1 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-[0.98] ${
                user.status
                  ? "bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-100"
                  : "bg-green-600 text-white shadow-lg shadow-green-200 hover:bg-green-700"
              }`}
            >
              {user.status ? "Suspend Account" : "Activate Account"}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const AutoField = ({ label, value, icon }) => (
  <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
    <div className="flex items-center gap-1.5 mb-1">
      <span className="material-symbols-outlined text-[14px] text-slate-300">
        {icon}
      </span>
      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
        {label}
      </span>
    </div>
    <p className="text-sm font-bold text-[#0B1C2D]">{value}</p>
  </div>
);

const InfoRow = ({ icon, label, value, isLink = false }) => (
  <div className="flex items-start gap-3">
    <span className="material-symbols-outlined text-[18px] text-slate-300 mt-0.5">
      {icon}
    </span>
    <div className="flex flex-col overflow-hidden">
      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter leading-none">
        {label}
      </span>
      {isLink ? (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-bold text-blue-600 mt-1.5 truncate hover:underline"
        >
          {value || "—"}
        </a>
      ) : (
        <span className="text-xs font-bold text-slate-700 mt-1.5 leading-tight truncate">
          {value || "—"}
        </span>
      )}
    </div>
  </div>
);

export default UserModal;
